/* Tela de chat IA — suporta multimodal, modo SOAP, indicadores de plantão */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AttachmentClip } from './AttachmentClip';
import { DocumentActionBar } from './DocumentActionBar';
import { PaywallCinematic } from './PaywallCinematic';
import { TrialDoors, type TrialDoor } from './TrialDoors';
import { TrialEmailGate } from './TrialEmailGate';
import { appendEstiloExemplo } from '../utils/localProfile';
import type { SendMessageOptions } from '../api/chat';
import type {
  ChatMessage,
  ChatError,
  CreditsInfo,
  AttachmentFile,
  DocRequestType,
} from '../types';

/**
 * Props do trial v2 (onboarding in-chat) — TODAS opcionais/aditivas.
 * Prop ausente ou null = comportamento atual exato (flag off / logado).
 */
export interface ChatViewTrialV2 {
  /** Duas portas do primeiro contato (anônimo, chat vazio, dia 0-1) */
  doorsVisible: boolean;
  /** Gate de email in-chat (anônimo dia 2-3) — undefined = sem gate */
  emailGate?: {
    active: boolean;
    onRequestCode: (email: string) => Promise<void>;
    onVerifyCode: (email: string, code: string) => Promise<void>;
    /** Locale da UI — usado pelo chip "Continuar com Google" (opcional) */
    locale?: string;
  };
  /** Fim do trial (dia 4+) — paywall proativo no load + framing com casos */
  trialEnded?: {
    /** true = /api/trial/state já diz dia 4+ (paywall ao carregar) */
    active: boolean;
    /** Casos corridos no trial — framing de investimento no paywall */
    casesCount: number;
    /**
     * Nonce de reabertura (02/07/2026): quando incrementa (>0), o paywall
     * proativo volta a aparecer mesmo se o médico já tinha dispensado —
     * usado quando o voice pair-init devolve 403 trial_ended.
     */
    reopenNonce?: number;
  };
}

/**
 * Linkify SÓ pra mensagens locais (kind='local', templates i18n do trial) —
 * o reforço do dia 3 traz o link da extensão CWS. Mensagens do LLM seguem
 * renderizadas como texto puro (comportamento atual intacto).
 */
function linkifyLocalContent(text: string): React.ReactNode {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--violet)',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
          wordBreak: 'break-all',
        }}
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

interface ChatViewProps {
  messages: ChatMessage[];
  loading: boolean;
  error: ChatError | null;
  creditsInfo: CreditsInfo | null;
  /** true = modo prontuário SOAP ativo */
  soapMode: boolean;
  /** Contexto do plantão para indicador visual */
  plantaoContext: string | null;
  onSend: (
    content: string,
    attachments?: AttachmentFile[],
    options?: SendMessageOptions,
  ) => Promise<void>;
  onClearSession: () => void;
  onClearError: () => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Trial v2 — null/undefined = fluxo atual intacto */
  trialV2?: ChatViewTrialV2 | null;
  /**
   * Callback do PRIMEIRO anexo do médico (marker em localStorage) —
   * o App insere mensagem local do Dr. Pera explicando o fluxo de exames.
   * Opcional/aditivo: ausente = comportamento atual exato.
   */
  onFirstAttachment?: () => void;
}

/** Marker localStorage — nudge do primeiro anexo dispara UMA vez por browser */
const FIRST_ATTACH_MARKER_KEY = 'drpera-ia-first-attach-nudge-v1';

/* Constantes do paste handler — fora do componente pra ficarem estáveis
   entre renders (evita warning react-hooks/exhaustive-deps no useCallback).
   20/05/2026: adicionado application/pdf (até 32MB) — coerente com AttachmentClip. */
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
const ACCEPTED_PASTE_TYPES = [...ACCEPTED_IMAGE_TYPES, 'application/pdf'] as const;
type AcceptedPasteType = (typeof ACCEPTED_PASTE_TYPES)[number];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;       // imagens — limite Anthropic
const MAX_PDF_BYTES = 32 * 1024 * 1024;        // PDFs — limite Anthropic 32MB / 100 páginas
const MAX_ATTACHMENTS = 3;

/**
 * Converte Blob/File pra base64 puro (sem prefixo data:...;base64,).
 * Usado pelo paste handler do textarea e (futuramente) pelo upload manual.
 */
function fileToBase64(file: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader result não é string'));
        return;
      }
      // dataURL = "data:image/png;base64,XXX..." → pega só XXX
      const parts = result.split(',');
      resolve(parts[1] ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/** Dots de loading animados — estilo CRT */
function LoadingDots() {
  return (
    <div
      aria-label="Dr. Pera está pensando"
      style={{
        padding: '12px 20px',
        color: 'var(--violet)',
        fontFamily: 'var(--font-sans)',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <span style={{ animation: 'cursor-blink 0.6s step-end infinite' }}>●</span>
      <span style={{ animation: 'cursor-blink 0.6s step-end 0.2s infinite' }}>●</span>
      <span style={{ animation: 'cursor-blink 0.6s step-end 0.4s infinite' }}>●</span>
    </div>
  );
}

/** Modal de paywall — créditos esgotados */
function PaywallModal({ t, onClose }: { t: ChatViewProps['t']; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('paywall.title')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(34, 37, 50, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 500,
        padding: '20px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          maxWidth: '400px',
          width: '100%',
          border: '1px solid rgba(124,124,248,0.40)',
          boxShadow: '0 0 0 1px rgba(124,124,248,0.15)',
          background: 'var(--ink-soft)',
          padding: '32px 28px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            fontWeight: 600,
            color: '#FFFFFF',
            marginBottom: '14px',
            letterSpacing: '0.05em',
          }}
        >
          {t('paywall.title')}
        </div>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '14px',
            color: 'var(--ink-light)',
            marginBottom: '24px',
            lineHeight: 1.6,
          }}
        >
          {t('paywall.description')}
        </p>
        <a
          href="https://drpera.com.br/precos"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            padding: '12px',
            background: 'var(--cta-blue)',
            color: '#FFFFFF',
            fontFamily: 'var(--font-sans)',
            fontSize: '14px',
            fontWeight: 600,
            textDecoration: 'none',
            letterSpacing: '0.02em',
            marginBottom: '12px',
            boxShadow: 'none',
          }}
        >
          {t('paywall.cta.subscribe')}
        </a>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-mute)',
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          {t('login.cta.back')}
        </button>
      </div>
    </div>
  );
}

/** Modal de fair use excedido */
function FairUseModal({ t, onClose }: { t: ChatViewProps['t']; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('fairUse.title')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(34, 37, 50, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 500,
        padding: '20px',
      }}
    >
      <div
        style={{
          maxWidth: '380px',
          width: '100%',
          border: '1px solid var(--ink-mute)',
          background: 'var(--ink-soft)',
          padding: '28px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--ink-light)',
            marginBottom: '12px',
          }}
        >
          {t('fairUse.title')}
        </div>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '14px',
            color: 'var(--ink-mute)',
            marginBottom: '20px',
          }}
        >
          {t('fairUse.description')}
        </p>
        <button
          onClick={onClose}
          style={{
            padding: '10px 24px',
            background: 'var(--ink)',
            border: '1px solid var(--ink-mute)',
            color: 'var(--ink-light)',
            fontFamily: 'var(--font-sans)',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          {t('fairUse.cta')}
        </button>
      </div>
    </div>
  );
}

export function ChatView({
  messages,
  loading,
  error,
  creditsInfo,
  soapMode,
  plantaoContext,
  onSend,
  onClearSession,
  onClearError,
  t,
  trialV2 = null,
  onFirstAttachment,
}: ChatViewProps) {
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  /**
   * Trial v2 — porta escolhida no primeiro contato anônimo.
   * 'none' = portas visíveis (se doorsVisible); 'A' = colar prontuário
   * exemplo; 'B' = começar com modelo padrão.
   */
  const [doorMode, setDoorMode] = useState<'none' | TrialDoor>('none');
  /** Feedback inline quando paste rejeita (>5MB ou tipo não suportado ou >3 limite). */
  const [pasteError, setPasteError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Ref sempre atualizada pra ler attachments.length sem stale closure. */
  const attachmentsRef = useRef<AttachmentFile[]>(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Scroll automático pro final quando nova mensagem chega
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Foca no textarea ao montar
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  /**
   * Primeiro anexo do médico (clip, drag-and-drop OU paste) → dispara o
   * nudge do Dr. Pera explicando o que ele faz com exames. Marker em
   * localStorage garante que roda UMA vez por browser — se localStorage
   * falhar, melhor não repetir a mensagem (early return).
   */
  useEffect(() => {
    if (attachments.length === 0 || !onFirstAttachment) return;
    try {
      if (localStorage.getItem(FIRST_ATTACH_MARKER_KEY) !== null) return;
      localStorage.setItem(FIRST_ATTACH_MARKER_KEY, new Date().toISOString());
    } catch {
      return;
    }
    onFirstAttachment();
  }, [attachments.length, onFirstAttachment]);

  /**
   * Paste handler — Ctrl+V de imagem (Print Screen ou copy de outra aba).
   *
   * Validações (idênticas ao botão de upload manual):
   *   - Tipos aceitos: image/png, image/jpeg, image/webp, image/gif (whitelist)
   *   - Tamanho máx: 5 MB por arquivo
   *   - Limite total: 3 anexos por mensagem
   *
   * Stale closure: lemos attachments via ref (não dep do useCallback) pra
   * sempre ter o valor atual, mesmo durante re-renders rápidos.
   *
   * Feedback: setPasteError dispara banner sutil acima do textarea por 3.5s.
   */
  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      // 20/05/2026: aceita imagens + PDFs no paste. PDFs vão como
      // ChatContentBlock type 'document' (Anthropic multimodal).
      const acceptedItems = Array.from(items).filter(
        (item) =>
          item.type.startsWith('image/') || item.type === 'application/pdf',
      );
      if (acceptedItems.length === 0) return; // texto normal — paste default segue

      e.preventDefault();

      const atual = attachmentsRef.current.length;
      const vagas = MAX_ATTACHMENTS - atual;
      if (vagas <= 0) {
        setPasteError(t('chat.paste_error_limit', { defaultValue: `Máximo de ${MAX_ATTACHMENTS} anexos por mensagem.` }));
        window.setTimeout(() => setPasteError(null), 3500);
        return;
      }

      const files = acceptedItems
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length === 0) return;

      const novos: AttachmentFile[] = [];
      let rejeitadosTipo = 0;
      let rejeitadosTamanho = 0;

      for (const file of files.slice(0, vagas)) {
        // Whitelist explícita — evita cast cego pra mediaType.
        if (!(ACCEPTED_PASTE_TYPES as readonly string[]).includes(file.type)) {
          rejeitadosTipo++;
          continue;
        }
        // Limite varia por tipo: imagens 5MB, PDFs 32MB.
        const isPdf = file.type === 'application/pdf';
        const limit = isPdf ? MAX_PDF_BYTES : MAX_IMAGE_BYTES;
        if (file.size > limit) {
          rejeitadosTamanho++;
          continue;
        }
        try {
          const base64 = await fileToBase64(file);
          const defaultName = isPdf ? 'documento.pdf' : 'clipboard.png';
          novos.push({
            id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            filename: file.name || defaultName,
            mediaType: file.type as AcceptedPasteType,
            base64,
            sizeBytes: file.size,
          });
        } catch {
          // FileReader pode falhar em casos extremos — não trava, só pula.
          rejeitadosTamanho++; // genérico
        }
      }

      if (novos.length > 0) {
        setAttachments((prev) => [...prev, ...novos]);
      }

      // Mostra feedback se algo foi rejeitado
      if (rejeitadosTipo > 0 || rejeitadosTamanho > 0) {
        const msg =
          rejeitadosTipo > 0
            ? t('chat.paste_error_type', { defaultValue: 'Tipo de imagem não suportado (PNG/JPEG/WEBP/GIF).' })
            : t('chat.paste_error_size', { defaultValue: 'Imagem maior que 5 MB.' });
        setPasteError(msg);
        window.setTimeout(() => setPasteError(null), 3500);
      }
    },
    [t],
  );

  const handleSend = useCallback(async () => {
    const content = input.trim();
    if ((!content && attachments.length === 0) || loading) return;
    // Trial v2: enquanto o gate de email está ativo, nada sai pelo chat
    if (trialV2?.emailGate?.active === true) return;
    setInput('');
    const attachsToSend = [...attachments];
    setAttachments([]);

    let toSend = content;
    // Trial v2 porta A — 1º envio é o prontuário-exemplo do médico:
    // (1) anexa o estilo ao perfil localStorage (recorte 600 chars) + push
    //     server best-effort; (2) envia com prefixo natural pro LLM aprender.
    // Conta como caso 1 do trial — está ok (decisão de spec).
    if (doorMode === 'A' && content && messages.length === 0) {
      appendEstiloExemplo(content);
      toSend = `${t('trial.doors.doorA.chatPrefix')}\n\n${content}`;
      setDoorMode('none');
    }

    await onSend(toSend, attachsToSend.length > 0 ? attachsToSend : undefined);
    textareaRef.current?.focus();
  }, [input, attachments, loading, onSend, doorMode, messages.length, t, trialV2]);

  /**
   * Barra "Gerar documento" — envia pelo chat o pedido natural CFM-safe
   * ("Gera o rascunho de X... pra eu revisar e assinar") + doc_request no
   * body (telemetria backend). Label do tipo em minúscula dentro da frase.
   */
  const handleGenerateDocument = useCallback(
    (docType: DocRequestType) => {
      if (loading) return;
      const label = t(`docbar.types.${docType}`);
      const pedido = t('docbar.request', { docType: label.toLocaleLowerCase() });
      void onSend(pedido, undefined, { docRequest: docType });
    },
    [loading, onSend, t],
  );

  // Enter quebra linha, Ctrl+Enter envia
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  }

  // Debug: ?paywall=trial ou ?paywall=exhausted força mostrar paywall (DEV/QA)
  const debugPaywall =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('paywall')
      : null;

  /* ── Trial v2: fim do trial (dia 4+) ──
     Dispara pelo erro do chat (402 registrado / 403 anônimo, ambos
     error='trial_ended') OU proativamente pelo estado no load. O paywall
     proativo é dispensável (backToChat), mas o chat segue bloqueado
     server-side — reabre no próximo envio. */
  const trialEndedInfo = trialV2?.trialEnded;
  const [trialPaywallDismissed, setTrialPaywallDismissed] = useState(false);

  /* Voice trial v2 (02/07/2026): pair-init 403 trial_ended incrementa o
     nonce no App → un-dismiss aqui reabre o paywall proativo. */
  const trialPaywallReopenNonce = trialEndedInfo?.reopenNonce ?? 0;
  useEffect(() => {
    if (trialPaywallReopenNonce > 0) {
      setTrialPaywallDismissed(false);
    }
  }, [trialPaywallReopenNonce]);

  const trialEndedTriggered =
    error?.type === 'trial_ended' ||
    (trialEndedInfo?.active === true && !trialPaywallDismissed);

  const showPaywall =
    error?.type === 'credits_exhausted' ||
    trialEndedTriggered ||
    debugPaywall === 'trial' ||
    debugPaywall === 'exhausted';

  const paywallVariant: 'trial-ended' | 'credits-exhausted' =
    debugPaywall === 'trial'
      ? 'trial-ended'
      : debugPaywall === 'exhausted'
      ? 'credits-exhausted'
      : trialEndedTriggered
      ? 'trial-ended'
      : creditsInfo?.isTrial
      ? 'trial-ended'
      : 'credits-exhausted';

  /** Framing de investimento no paywall (só trial v2 com casos > 0) */
  const paywallInvestmentNote =
    trialEndedTriggered && (trialEndedInfo?.casesCount ?? 0) > 0
      ? t('trial.paywall.investment', {
          casesCount: trialEndedInfo?.casesCount ?? 0,
        })
      : undefined;

  const showFairUse = error?.type === 'fair_use_exceeded';
  const isFirstMessage = messages.length === 0;

  /* ── Trial v2: duas portas do primeiro contato anônimo ── */
  const doorsAvailable = trialV2?.doorsVisible === true && isFirstMessage;
  const showDoorsChooser = doorsAvailable && doorMode === 'none';
  const showDoorAInstruction = doorsAvailable && doorMode === 'A';
  // Welcome padrão: fluxo atual OU porta B escolhida
  const showWelcome = isFirstMessage && (!doorsAvailable || doorMode === 'B');

  /* ── Trial v2: gate de email in-chat (anônimo dia 2-3) ──
     Enquanto ativo, o textarea principal fica desabilitado — o email/código
     entram SÓ pelos inputs do gate (jamais passam pelo endpoint de chat). */
  const emailGate = trialV2?.emailGate;
  const emailGateActive = emailGate?.active === true;

  // Borda coral no container do chat quando SOAP ativo
  const soapBorderStyle: React.CSSProperties = soapMode
    ? { boxShadow: 'inset 0 0 0 1px rgba(124,124,248,0.35)' }
    : {};

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        ...soapBorderStyle,
      }}
    >
      {/* Barra de controles do chat */}
      <div
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid var(--ink-soft)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* Indicador de modo SOAP */}
        {soapMode && (
          <div
            aria-live="polite"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '12px',
              color: 'var(--ink-light)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '12px' }}>⊕</span>
            {t('chat.soapModeIndicator')}
          </div>
        )}

        {/* Indicador de plantão ativo — estado "vivo" → glow ciano permitido */}
        {plantaoContext && (
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '12px',
              color: '#00D4FF',
              textShadow: '0 0 6px rgba(0, 212, 255, 0.6)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '12px' }}>⏱</span>
            {t('chat.plantaoIndicator')}
          </div>
        )}

        <div style={{ flex: 1 }} />

        <button
          onClick={onClearSession}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '12px',
            padding: '4px 10px',
            background: 'transparent',
            border: '1px solid var(--ink-soft)',
            color: 'var(--ink-mute)',
            cursor: 'pointer',
          }}
        >
          {t('chat.newSession')}
        </button>
      </div>

      {/* Lista de mensagens */}
      <div
        role="log"
        aria-live="polite"
        aria-label="Histórico do chat"
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: '20px',
          paddingBottom: '10px',
        }}
      >
        {/* Trial v2 — duas portas (anônimo, chat vazio, dia 0-1) */}
        {showDoorsChooser && (
          <TrialDoors t={t} onSelect={setDoorMode} />
        )}

        {/* Trial v2 — porta A escolhida: instrução pra colar o prontuário */}
        {showDoorAInstruction && (
          <div
            style={{
              padding: '10px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            <div
              style={{
                maxWidth: '80%',
                padding: '10px 14px',
                border: '1px solid rgba(124,124,248,0.30)',
                color: 'var(--ink-light)',
                fontFamily: 'var(--font-sans)',
                fontSize: '14px',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              {t('trial.doors.doorA.instruction')}
            </div>
            <button
              type="button"
              onClick={() => setDoorMode('none')}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--ink-mute)',
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '2px 4px',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              {t('trial.doors.back')}
            </button>
          </div>
        )}

        {/* Mensagem de boas-vindas (fluxo atual OU porta B) */}
        {showWelcome && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: 'var(--ink-mute)',
              fontFamily: 'var(--font-sans)',
              fontSize: '14px',
            }}
          >
            <div style={{ marginBottom: '16px' }}>
              {/* Fix 24/05/2026 Dr. Lu: icone redondo coral sem fundo quadrado */}
              <img
                src="/icons/icon-128.png"
                alt="Mascote Dr. Pera"
                width={64}
                height={64}
                style={{
                  display: 'inline-block',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  filter: 'drop-shadow(0 0 6px rgba(124, 124, 248, 0.55))',
                  transition: 'filter 180ms ease-out',
                }}
              />
            </div>
            <p style={{ lineHeight: 1.8 }}>{t('chat.welcome')}</p>
            {/* Porta B: sugestão de primeiro caso */}
            {doorsAvailable && doorMode === 'B' && (
              <p
                style={{
                  marginTop: '12px',
                  fontSize: '13px',
                  color: 'var(--ink-light)',
                  lineHeight: 1.7,
                }}
              >
                {t('trial.doors.doorB.suggestion')}
              </p>
            )}
            {creditsInfo?.isTrial && (
              <p
                style={{
                  marginTop: '12px',
                  fontSize: '12px',
                  color: 'var(--ink-light)',
                  textShadow: 'none',
                }}
              >
                ◉ {t('credits.trial', { credits: creditsInfo.credits })}
              </p>
            )}
          </div>
        )}

        {/* Mensagens */}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              padding: '10px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: '4px',
            }}
          >
            {/* Thumbnails de anexos enviados */}
            {msg.role === 'user' && msg.attachments && msg.attachments.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {msg.attachments.map((att) => {
                  const isImagem = att.mediaType.startsWith('image/');
                  return (
                    <div
                      key={att.id}
                      style={{
                        width: '48px',
                        height: '48px',
                        border: '1px solid var(--ink-mute)',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--ink-soft)',
                      }}
                    >
                      {isImagem ? (
                        <img
                          src={`data:${att.mediaType};base64,${att.base64}`}
                          alt={att.filename}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontSize: '12px',
                            color: 'var(--ink-mute)',
                          }}
                        >
                          PDF
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bolha de texto */}
            {msg.content && (
              <div
                style={{
                  maxWidth: '80%',
                  padding: '10px 14px',
                  background: msg.role === 'user' ? 'var(--ink-soft)' : 'transparent',
                  border:
                    msg.role === 'assistant'
                      ? '1px solid rgba(124,124,248,0.30)'
                      : '1px solid var(--ink-mute)',
                  color: msg.role === 'user' ? 'var(--ink-light)' : 'var(--ink-light)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '14px',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {/* Mensagens locais (trial v2): linkify URLs (ex.: link CWS) */}
                {msg.kind === 'local' ? linkifyLocalContent(msg.content) : msg.content}
              </div>
            )}

            {/* Barra de ações pós-resposta — só assistant REAL (não-local) */}
            {msg.role === 'assistant' && msg.kind !== 'local' && msg.content && (
              <DocumentActionBar
                content={msg.content}
                disabled={loading || emailGateActive}
                onGenerateDocument={handleGenerateDocument}
                t={t}
              />
            )}
          </div>
        ))}

        {/* Trial v2 — email in-chat (anônimo dia 2-3, HTTP 403 email_required) */}
        {emailGateActive && emailGate && (
          <TrialEmailGate
            onRequestCode={emailGate.onRequestCode}
            onVerifyCode={emailGate.onVerifyCode}
            locale={emailGate.locale}
            t={t}
          />
        )}

        {/* Loading dots */}
        {loading && <LoadingDots />}

        {/* Âncora de scroll */}
        <div ref={messagesEndRef} />
      </div>

      {/* Erro genérico inline (não 402/429; email_required tem gate próprio) */}
      {error && !showPaywall && !showFairUse && error.type !== 'email_required' && (
        <div
          role="alert"
          style={{
            padding: '10px 20px',
            background: 'rgba(124,124,248,0.06)',
            borderTop: '1px solid rgba(124,124,248,0.30)',
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            color: '#7C7CF8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>✗ {error.message}</span>
          <button
            onClick={onClearError}
            aria-label="Fechar erro"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#7C7CF8',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: 'var(--font-sans)',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Área de input + anexos */}
      <div
        style={{
          borderTop: '1px solid var(--ink-soft)',
          padding: '10px 16px 14px',
          background: 'var(--ink)',
        }}
      >
        {/* Clip + thumbnails acima do textarea */}
        <AttachmentClip
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          t={t}
          disabled={loading}
        />

        {/* Feedback de paste rejeitado — sutil, autohide 3.5s */}
        {pasteError !== null && (
          <div
            role="alert"
            aria-live="polite"
            style={{
              marginTop: 6,
              padding: '6px 10px',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: '#7C7CF8',
              background: 'rgba(124,124,248,0.06)',
              border: '1px solid rgba(124,124,248,0.30)',
              borderRadius: 4,
              textShadow: 'none',
            }}
          >
            ⚠ {pasteError}
          </div>
        )}

        {/* Linha do textarea + botão enviar */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginTop: '4px' }}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={
              showDoorAInstruction
                ? t('trial.doors.doorA.placeholder')
                : t('chat.placeholder')
            }
            rows={3}
            disabled={loading || emailGateActive}
            aria-label={t('chat.placeholder')}
            style={{
              flex: 1,
              resize: 'none',
              padding: '10px 14px',
              background: 'var(--ink-soft)',
              border: soapMode
                ? '1px solid rgba(124,124,248,0.30)'
                : '1px solid var(--ink-mute)',
              color: 'var(--ink-light)',
              fontFamily: 'var(--font-sans)',
              fontSize: '14px',
              lineHeight: 1.5,
              outline: 'none',
              transition: 'border-color 180ms ease-out',
            }}
          />
          <button
            onClick={handleSend}
            disabled={(!input.trim() && attachments.length === 0) || loading || emailGateActive}
            aria-label={t('chat.send')}
            title="Ctrl+Enter para enviar"
            style={{
              padding: '10px 16px',
              background:
                (input.trim() || attachments.length > 0) && !loading && !emailGateActive
                  ? 'var(--cta-blue)'
                  : 'var(--ink-soft)',
              border: 'none',
              color:
                (input.trim() || attachments.length > 0) && !loading && !emailGateActive
                  ? '#FFFFFF'
                  : 'var(--ink-mute)',
              cursor:
                (input.trim() || attachments.length > 0) && !loading && !emailGateActive
                  ? 'pointer'
                  : 'not-allowed',
              fontFamily: 'var(--font-sans)',
              fontSize: '18px',
              lineHeight: 1,
              flexShrink: 0,
              transition: 'background 180ms, box-shadow 180ms',
              boxShadow: 'none',
            }}
          >
            ➤
          </button>
        </div>
      </div>

      {/* Modais */}
      {showPaywall && (
        <PaywallCinematic
          t={t}
          onClose={() => {
            onClearError();
            // Trial v2: dispensa o paywall proativo (chat segue bloqueado
            // server-side; paywall reabre no próximo envio)
            setTrialPaywallDismissed(true);
            // Limpa query param de debug se presente
            if (debugPaywall && typeof window !== 'undefined') {
              const url = new URL(window.location.href);
              url.searchParams.delete('paywall');
              window.history.replaceState({}, '', url.toString());
            }
          }}
          variant={paywallVariant}
          investmentNote={paywallInvestmentNote}
        />
      )}
      {showFairUse && <FairUseModal t={t} onClose={onClearError} />}
    </div>
  );
}
